/* ── Midnight Observatory — Login ── */

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background:
    radial-gradient(ellipse 60% 50% at 50% 40%, rgba(34, 211, 238, 0.06) 0%, transparent 70%),
    linear-gradient(135deg, #060a14 0%, #0b1120 50%, #0f172a 100%);
  position: relative;
}

[data-theme="light"] .container {
  background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%);
}

/* Subtle cyan glow behind card — dark mode only */
.container::before {
  content: '';
  position: absolute;
  width: 480px;
  height: 480px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.08) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

[data-theme="light"] .container::before {
  display: none;
}

.card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.1);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 24px;
  padding: 2rem;
  animation: fadeInUp 0.6s ease both;
}

[data-theme="light"] .card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 8px 40px rgba(15, 23, 42, 0.08);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.backLink {
  display: inline-block;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.875rem;
  font-family: var(--font-body);
  margin-bottom: 1.25rem;
  transition: color 0.2s;
}

.backLink:hover {
  color: var(--text-primary);
}

.logoSection {
  text-align: center;
  margin-bottom: 1.25rem;
}

.logoSection img {
  width: 48px;
  height: 48px;
}

.title {
  font-size: 1.6rem;
  font-weight: 700;
  font-family: var(--font-heading);
  margin: 0.75rem 0 0.25rem;
  color: var(--text-primary);
}

.subtitle {
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.95rem;
  font-family: var(--font-body);
}

.googleBtn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(148, 163, 184, 0.15);
  color: var(--text-primary);
  padding: 0.8rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  font-family: var(--font-body);
  cursor: pointer;
  transition: all 0.2s;
}

.googleBtn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(148, 163, 184, 0.25);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

[data-theme="light"] .googleBtn {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  color: var(--text-primary);
}

[data-theme="light"] .googleBtn:hover {
  background: #f8fafc;
  border-color: rgba(15, 23, 42, 0.2);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-secondary);
  font-family: var(--font-body);
}

.field input {
  padding: 0.75rem 1rem;
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.4);
  color: var(--text-primary);
  font-size: 1rem;
  font-family: var(--font-body);
  transition: all 0.2s;
}

.field input:focus {
  outline: none;
  border-color: rgba(34, 211, 238, 0.4);
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.08);
  background: rgba(15, 23, 42, 0.5);
}

.field input::placeholder {
  color: rgba(148, 163, 184, 0.4);
}

.altchaField {
  --altcha-max-width: 100%;
  --altcha-border-radius: 10px;
  --altcha-border-color: rgba(148, 163, 184, 0.15);
  --altcha-color-base: rgba(15, 23, 42, 0.35);
  --altcha-color-base-content: var(--text-primary);
  --altcha-color-border: rgba(148, 163, 184, 0.15);
  --altcha-color-primary: #0891b2;
  --altcha-color-success: #10b981;
  --altcha-color-error: var(--color-error);
  color: var(--text-primary);
}

.altchaField altcha-widget {
  display: block;
  width: 100%;
}

[data-theme="light"] .altchaField {
  --altcha-color-base: var(--bg-secondary);
  --altcha-border-color: rgba(15, 23, 42, 0.08);
  --altcha-color-border: rgba(15, 23, 42, 0.08);
}

[data-theme="light"] .field input {
  background: var(--bg-secondary);
  border-color: rgba(15, 23, 42, 0.08);
  color: var(--text-primary);
}

[data-theme="light"] .field input:focus {
  border-color: var(--text-accent);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.08);
}

[data-theme="light"] .field input::placeholder {
  color: rgba(15, 23, 42, 0.3);
}

.forgotLink {
  align-self: flex-end;
  color: var(--text-accent);
  opacity: 0.7;
  text-decoration: none;
  font-size: 0.875rem;
  transition: opacity 0.2s;
}

.forgotLink:hover {
  opacity: 1;
}

.error {
  background: var(--color-error-light);
  border: 1px solid var(--color-error-border);
  color: var(--color-error);
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
}

[data-theme="light"] .error {
  background: var(--color-error-light);
  border-color: var(--color-error-border);
  color: var(--color-error);
}

.submitBtn {
  background: linear-gradient(135deg, #0891b2, #22d3ee);
  color: #fff;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-body);
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.25rem;
}

.submitBtn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(34, 211, 238, 0.3);
}

.submitBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.divider {
  display: flex;
  align-items: center;
  margin: 1rem 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-color);
}

.divider span {
  padding: 0 1rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.switchBtn {
  width: 100%;
  margin-top: 1rem;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 0.75rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: var(--font-body);
  cursor: pointer;
  transition: all 0.2s;
}

.switchBtn:hover {
  border-color: rgba(148, 163, 184, 0.25);
  color: var(--text-primary);
  background: rgba(148, 163, 184, 0.05);
}

[data-theme="light"] .switchBtn:hover {
  border-color: rgba(15, 23, 42, 0.15);
  color: var(--text-primary);
  background: rgba(15, 23, 42, 0.02);
}

/* Value props — signup mode only */
.valueProps {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 1.25rem;
  padding: 0.65rem 0.85rem;
  background: rgba(34, 211, 238, 0.06);
  border: 1px solid rgba(34, 211, 238, 0.12);
  border-radius: 10px;
}

[data-theme="light"] .valueProps {
  background: rgba(8, 145, 178, 0.04);
  border-color: rgba(8, 145, 178, 0.1);
}

.valueProp {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: var(--font-body);
}

.valueCheck {
  color: #22d3ee;
  font-weight: 600;
  font-size: 0.875rem;
}

[data-theme="light"] .valueCheck {
  color: #0891b2;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .container {
    padding: 0.75rem;
    align-items: flex-start;
    padding-top: 1.5rem;
  }

  .card {
    padding: 1.25rem;
    border-radius: 18px;
  }

  .title {
    font-size: 1.4rem;
  }

  .logoSection {
    margin-bottom: 1rem;
  }

  .logoSection img {
    width: 36px;
    height: 36px;
  }

  .submitBtn {
    min-height: 44px;
  }

  .switchBtn {
    min-height: 44px;
  }

  .googleBtn {
    min-height: 44px;
  }

  .field input {
    min-height: 44px;
  }
}
